import type { CSSProperties } from 'react';

import type { VariantProp } from '../../common/colors';
import type { CSS } from '../../stitches.config';
import { getVariantStyles, variantIconColor } from '../../common/variant';
import { keyframes, styled } from '../../stitches.config';
import { transitions } from '../shared/styles';

/** Values accepted by Stitches for CSS inset properties (`top` / `right` / `bottom` / `left`). */
export type CalloutInsetValue = CSS['top'];

export type CalloutPosition = Partial<{
  top: CalloutInsetValue;
  right: CalloutInsetValue;
  bottom: CalloutInsetValue;
  left: CalloutInsetValue;
}>;

/** Map anchored edges → translate percentages so center of the dot meets the inset corner / edge. */
function resolveCalloutInsetAndTranslate(position: CalloutPosition | undefined): {
  inset: Partial<Pick<CSS, 'top' | 'right' | 'bottom' | 'left'>>;
  translateXPercent: string;
  translateYPercent: string;
} {
  if (position === undefined) {
    return {
      inset: { top: 0, left: 0 },
      translateXPercent: '-50%',
      translateYPercent: '-50%',
    };
  }

  const hasTop = position.top !== undefined;
  const hasBottom = position.bottom !== undefined;
  const hasLeft = position.left !== undefined;
  const hasRight = position.right !== undefined;

  const inset: Partial<Pick<CSS, 'top' | 'right' | 'bottom' | 'left'>> = {};

  if (hasTop) inset.top = position.top;
  else if (!hasBottom) inset.top = 0;

  if (hasBottom) inset.bottom = position.bottom;
  if (hasLeft) inset.left = position.left;
  else if (!hasRight) inset.left = 0;

  if (hasRight) inset.right = position.right;

  const translateXPercent =
    position.right !== undefined && position.left === undefined ? '50%' : '-50%';
  const translateYPercent =
    position.bottom !== undefined && position.top === undefined ? '50%' : '-50%';

  return {
    inset,
    translateXPercent,
    translateYPercent,
  };
}

/** Slow, low-amplitude pulse; translate axes come from `--callout-tx` / `--callout-ty`. */
const calloutAlive = keyframes({
  '0%, 100%': {
    opacity: 1,
    transform: 'translate(var(--callout-tx), var(--callout-ty)) scale(1)',
  },
  '50%': {
    opacity: 0.7,
    transform: 'translate(var(--callout-tx), var(--callout-ty)) scale(0.86)',
  },
});

const CalloutContainer = styled('div', {
  '--callout-tx': '-50%',
  '--callout-ty': '-50%',
  position: 'absolute',
  widthAll: '$8',
  heightAll: '$8',
  background: variantIconColor,
  borderRadius: '99rem',
  transition: transitions.control,
  transform: 'translate(var(--callout-tx), var(--callout-ty))',

  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${calloutAlive} 1.8s ease-in-out infinite`,
    backfaceVisibility: 'hidden',
  },

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
CalloutContainer.displayName = 'CalloutContainer';

export type CalloutProps = {
  /**
   * The position of the callout.
   */
  position?: CalloutPosition;
  /**
   * The variant of the callout.
   */
  variant: VariantProp;
};

export function Callout({ position, variant }: CalloutProps) {
  const { inset, translateXPercent, translateYPercent } = resolveCalloutInsetAndTranslate(position);

  return (
    <CalloutContainer
      variant={variant}
      css={inset}
      style={
        {
          '--callout-tx': translateXPercent,
          '--callout-ty': translateYPercent,
        } as CSSProperties
      }
    />
  );
}
